import { useCallback, useEffect, useState } from 'react';
import { coreRuntime } from '@core/runtime';
import {
  browserPushDeps,
  disableWebPush,
  enableWebPush,
  isWebPushOn,
  pushAvailability,
  readPushEnv,
  type EnableResult,
  type PushAvailability,
} from '@core/utils/webPush';

export interface WebPushState {
  availability: PushAvailability;
  /** Сервер принимает подписки (заданы VAPID-ключи). null — ещё не знаем. */
  serverEnabled: boolean | null;
  /** Уведомления этой карточки включены в этом браузере. */
  on: boolean;
  busy: boolean;
  enable(): Promise<EnableResult | 'error'>;
  disable(): Promise<boolean>;
}

/** Состояние «Уведомлений в браузере» для вошедшего клиента userId (PWA). В мини-аппе — availability 'off'. */
export function useWebPush(userId: string | null | undefined): WebPushState {
  const entryAllows = !!coreRuntime().capabilities.webPush;
  const [availability, setAvailability] = useState<PushAvailability>(() => pushAvailability(readPushEnv(entryAllows)));
  const [serverEnabled, setServerEnabled] = useState<boolean | null>(null);
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (availability === 'off' || !userId) return;
    let cancelled = false;
    const deps = browserPushDeps();
    deps
      .serverKey()
      .then((k) => !cancelled && setServerEnabled(k.enabled && !!k.publicKey))
      .catch(() => !cancelled && setServerEnabled(false));
    if (availability === 'available') {
      isWebPushOn(userId, deps)
        .then((v) => !cancelled && setOn(v))
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [availability, userId]);

  const enable = useCallback(async (): Promise<EnableResult | 'error'> => {
    if (!userId || busy) return 'error';
    setBusy(true);
    try {
      const r = await enableWebPush(userId);
      setOn(r === 'subscribed');
      if (r === 'denied' || r === 'unsupported') setAvailability(pushAvailability(readPushEnv(entryAllows)));
      if (r === 'disabled') setServerEnabled(false);
      return r;
    } catch {
      return 'error';
    } finally {
      setBusy(false);
    }
  }, [userId, busy, entryAllows]);

  const disable = useCallback(async (): Promise<boolean> => {
    if (!userId || busy) return false;
    setBusy(true);
    try {
      await disableWebPush(userId);
      setOn(false);
      return true;
    } catch {
      return false;
    } finally {
      setBusy(false);
    }
  }, [userId, busy]);

  return { availability, serverEnabled, on, busy, enable, disable };
}
